'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai/react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useTheme } from 'next-themes'
import { useCallback, useEffect, useState } from 'react'
import { useKey, useLocalStorage } from 'react-use'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import { Dialog, DialogContent } from '../ui/dialog'

const SLIDE_CONTENT = (name: string, isLight: boolean) => [
  {
    title: (
      <h3 className='font-pp-editorial-new font-extralight text-4xl text-primary'>
        gm, {name.split(' ')[0]}.
      </h3>
    ),
    description: (
      <span className='text-secondary'>
        Welcome to Polaris. You’re ready to build your portfolio and trade like
        a true pioneer.
      </span>
    ),
    image: (
      <Image
        src='https://s3.polaris.app/artworks/girl-backpack-field-house.png'
        alt=''
        fill
        className='object-cover'
        draggable={false}
        sizes='448px'
      />
    ),
  },
  {
    title: (
      <h3 className='font-pp-editorial-new font-extralight text-4xl text-primary'>
        Your <span className='italic'>entire</span> portfolio, unified.
      </h3>
    ),
    description: (
      <span className='text-secondary'>
        Track all of your wallet balances without having to reconnect them every
        time you return.
      </span>
    ),
    image: (
      <Image
        src={`https://s3.polaris.app/artworks/intro-balances${isLight ? '-light' : ''}.png`}
        alt=''
        width={448}
        height={404}
        className='object-cover absolute bottom-0'
        draggable={false}
      />
    ),
    containerClassName: 'bg-orange-700',
  },
  {
    title: (
      <h3 className='font-pp-editorial-new font-extralight text-4xl text-[#0A0F0D]'>
        Never miss <span className='italic'>critical</span> updates.
      </h3>
    ),
    description: (
      <span className='text-[#0A0F0D]'>
        Get real-time updates tailored to the assets you hold, because timing is
        everything.
      </span>
    ),
    image: (
      <Image
        src={`https://s3.polaris.app/artworks/intro-news${isLight ? '-light' : ''}.png`}
        alt=''
        width={448}
        height={324}
        className='object-cover absolute bottom-0'
        draggable={false}
      />
    ),
    containerClassName: 'bg-orange-100',
  },
  {
    title: (
      <h3 className='font-pp-editorial-new font-extralight text-4xl text-primary'>
        Ready to start <span className='italic'>trading</span>?
      </h3>
    ),
    description: (
      <span className='text-secondary'>
        Trade seamlessly, straight from your portfolio. Let’s dive in.
      </span>
    ),
    image: (
      <Image
        src='https://s3.polaris.app/artworks/monocular-cap-map.png'
        alt=''
        width={486}
        height={248}
        className='object-cover absolute bottom-[150px]'
        draggable={false}
      />
    ),
    containerClassName: 'bg-green-800',
  },
]

export function IntroDialogRoot({
  hasLoggedIn,
  name,
}: {
  hasLoggedIn: boolean
  name: string
}) {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const [viewed, setViewed] = useLocalStorage<boolean | undefined>(
    'intro-dialog-viewed',
    undefined,
  )
  const isOpen =
    viewed === undefined && hasLoggedIn && featureFlags.newLoginFlow

  if (!isOpen) return null

  return <IntroDialogContent setViewed={setViewed} name={name} />
}

function IntroDialogContent({
  setViewed,
  name,
}: {
  setViewed: (viewed: boolean) => void
  name: string
}) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const { theme } = useTheme()
  const isLight = theme === 'light'

  const nextSlide = useCallback(() => {
    setCurrentSlide((p) => {
      if (p === 3) return 0

      return p + 1
    })
  }, [])

  const prevSlide = useCallback(() => {
    setCurrentSlide((p) => {
      if (p === 0) return 3

      return p - 1
    })
  }, [])

  useKey('ArrowRight', nextSlide)
  useKey('ArrowLeft', prevSlide)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intended behavior, the interval should cleared even when the currentSlide changes to make sure that a manual invocation of nextSlide resets the interval
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide()
    }, 1000 * 5)

    return () => clearInterval(interval)
  }, [nextSlide, currentSlide])

  const handleClose = () => {
    setViewed(true)
  }

  const { title, description, image, containerClassName } = SLIDE_CONTENT(
    name,
    isLight,
  )[currentSlide]

  return (
    <Dialog open>
      <DialogContent
        className={cn(
          'max-w-[448px] outline-none max-h-[640px] transition-all dark',
          containerClassName,
        )}
        closeAction={handleClose}
        overlayClassName='!pointer-events-auto'
        hideHeader
      >
        <div
          className='absolute left-0 w-1/3 h-full z-10'
          onClick={prevSlide}
        />
        <div
          className='absolute right-0 w-2/3 h-full z-10'
          onClick={nextSlide}
        />
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {image}
          </motion.div>
        </AnimatePresence>
        <div className='absolute top-0 left-0 right-0 w-full pt-3.5 px-7 z-10'>
          <div className='flex items-center gap-0.5'>
            {Array.from({ length: 4 }).map((_, index) => (
              <TimedSlide
                key={index}
                index={index}
                currentSlide={currentSlide}
              />
            ))}
          </div>
        </div>
        <div className='relative w-full h-full pointer-events-none'>
          <div className='flex flex-col gap-3 text-center relative z-10 mt-16 px-8'>
            <AnimatePresence mode='wait'>
              <motion.div
                key={`title-desc-${currentSlide}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {title}
                {description}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className='p-8 pt-0 absolute z-10 w-full bottom-0'>
          <Button
            type='button'
            variant='primary'
            size='md'
            className='w-full'
            onClick={handleClose}
          >
            Start trading
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function TimedSlide({
  index,
  currentSlide,
}: {
  currentSlide: number
  index: number
}) {
  return (
    <div
      className={cn('relative h-0.5 w-full bg-border2 rounded-sm', {
        'bg-primary': currentSlide >= index + 1,
        'bg-[#1B240052]': currentSlide <= index + 1 && currentSlide === 2,
        'bg-[#0A0F0D]': currentSlide >= index + 1 && currentSlide === 2,
      })}
    >
      {currentSlide === index && (
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: 5, ease: 'linear' }}
          className={cn('absolute inset-0 bg-primary z-10 h-0.5 rounded-sm', {
            'bg-[#0A0F0D]': currentSlide === 2,
          })}
        />
      )}
    </div>
  )
}
